/**
 * As regras que escolhem, entre as residências, as casas que uma tela oferece.
 *
 * Papel: aqui ficam `NOME_DA_FAZENDA` e `casasDaFazenda`, que decidem de que
 * casa são o Calendário e as Manutenções da Fazenda Boa Vista, e
 * `casasQueRecebemRegistro`, que decide em que casas um pedido de compra ou um
 * orçamento pode ser gravado.
 *
 * Como usar: toda tela que escolhe casa pelo nome da fazenda, ou que oferece a
 * lista de casas num formulário de pedido ou de orçamento, deverá passar por
 * estas funções, e nunca repetir a comparação por conta própria nem cair em
 * `casas[0]`. A lista de `useResidencias` chega ordenada pelo continente, com
 * quem não está dentro de nada primeiro, e por isso a primeira casa dela pode
 * ser uma casa de fora da fazenda — abrir o calendário ou sugerir o pedido
 * nela grava o registro na casa errada sem nenhum aviso.
 *
 * As funções recebem só a parte da residência que leem, e não a
 * `Residencia` inteira, para que uma tela sem acesso a `useResidencias` possa
 * passar as casas que tira dos próprios registros — um pedido traz a casa sem
 * `dentroDe`, e para as regras isso quer dizer que ela não está dentro de
 * nada.
 */

/**
 * O nome da residência que contém a casa de campo, como está no cadastro.
 *
 * É regra de negócio, e não dado a esconder: o Calendário e as Manutenções
 * existem para a casa que fica dentro deste lugar, e o nome dos dois módulos
 * já o carrega. Renomear a residência no cadastro sem renomear aqui deixa as
 * duas telas sem casa nenhuma, no estado de quem ainda não cadastrou a casa.
 */
export const NOME_DA_FAZENDA = 'Fazenda Boa Vista';

/** A parte de uma residência que `casasDaFazenda` lê. */
interface CasaComContinente {
  nome: string;
  dentroDe: { nome: string } | null;
}

/** A parte de uma residência que `casasQueRecebemRegistro` lê. */
interface CasaQuePodeConter {
  id: string;
  /** O lugar que contém a casa; ausente quando quem a traz não o conhece. */
  dentroDe?: { id: string } | null;
}

/**
 * As casas da Fazenda Boa Vista: as que ficam dentro dela e a própria fazenda.
 *
 * Quem chama é a página do Calendário, que fica com a primeira destas que tem
 * alguém na escala, e a das Manutenções, que fica com a primeira que tem
 * registro — as duas nunca olham uma casa de fora da fazenda, mesmo que ela
 * venha antes na lista e tenha equipe ou histórico. As casas de dentro vêm
 * antes da própria fazenda, na ordem em que chegaram, porque é delas que as
 * duas telas são; a fazenda só é escolhida quando é a única, ou a única com o
 * que a tela procura.
 *
 * @param casas - As residências de `useResidencias`, na ordem da API.
 * @returns As casas cujo `nome` é `NOME_DA_FAZENDA` ou cujo `dentroDe` tem esse
 *   nome, com as de dentro primeiro; vazio quando a fazenda não está
 *   cadastrada, e a tela deverá mostrar então o estado de quem não tem casa.
 */
export function casasDaFazenda<T extends CasaComContinente>(casas: readonly T[]): T[] {
  const dentro = casas.filter((casa) => casa.dentroDe?.nome === NOME_DA_FAZENDA);
  const aPropria = casas.filter(
    (casa) => casa.nome === NOME_DA_FAZENDA && casa.dentroDe?.nome !== NOME_DA_FAZENDA,
  );
  return [...dentro, ...aPropria];
}

/**
 * As casas em que um pedido de compra ou um orçamento pode ser gravado: as que
 * não contêm outra.
 *
 * Uma residência que contém outra é o lugar, e não a casa — a Fazenda Boa
 * Vista contém a casa onde a despesa de fato acontece. Um pedido gravado no
 * lugar tem a despesa lançada numa casa que não é a do caixa, e ela some do
 * extrato de Finanças. Quem chama é o formulário de pedido, para os `Chip` de
 * casa, e o de orçamento, para o `<select>` de casa.
 *
 * @param casas - As residências a filtrar, na ordem em que deverão aparecer.
 * @param idDaCasaDoRegistro - A casa do registro em edição, que continua na
 *   lista mesmo sendo um lugar que contém outra — um registro antigo gravado
 *   assim não pode perder a casa ao ser aberto e salvo de novo; `null` num
 *   registro novo ou sem casa.
 * @returns As casas de `casas` que nenhuma outra tem como `dentroDe`, mais a do
 *   registro, na ordem de `casas`.
 */
export function casasQueRecebemRegistro<T extends CasaQuePodeConter>(
  casas: readonly T[],
  idDaCasaDoRegistro: string | null,
): T[] {
  const continentes = new Set(
    casas.flatMap((casa) =>
      casa.dentroDe === undefined || casa.dentroDe === null ? [] : [casa.dentroDe.id],
    ),
  );
  return casas.filter((casa) => casa.id === idDaCasaDoRegistro || !continentes.has(casa.id));
}

/**
 * As casas distintas de uma lista de registros, uma vez cada, na ordem em que
 * aparecem pela primeira vez.
 *
 * Quem chama é a página de um formulário quando `useResidencias` falha — quem
 * não tem `ver` em Cadastros-base recebe 403 dela —, para montar a lista de
 * casas com o que os próprios registros do módulo já trazem.
 *
 * @param casas - A casa de cada registro, com `null` no registro sem casa.
 * @returns As casas sem repetição de `id` e sem os nulos.
 */
export function casasDistintas<T extends { id: string }>(casas: readonly (T | null)[]): T[] {
  const vistas = new Map<string, T>();
  for (const casa of casas) {
    if (casa !== null && !vistas.has(casa.id)) vistas.set(casa.id, casa);
  }
  return [...vistas.values()];
}
